<template>
    <div v-if="entries.length" class="search-status" :class="`text-${summary.color}`">
        <div class="live-status-label">Live search status:</div>
        <div class="d-flex align-items-center gap-1">
            <span role="status" aria-live="polite">{{ summary.label }}</span>
            <button
                type="button"
                class="search-status-button"
                aria-label="More info about live search"
                title="Live-search details"
                aria-haspopup="dialog"
                @click="$refs.details.showModal()"
            >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="1.5" />
                    <path d="M10 9v5" stroke="currentColor" stroke-width="1.5" />
                    <circle cx="10" cy="6" r="1" fill="currentColor" />
                </svg>
            </button>
        </div>
        <dialog
            ref="details"
            class="search-status-dialog"
            aria-labelledby="search-status-title"
            @click="closeOnBackdrop"
        >
            <div class="modal-header">
                <h5 id="search-status-title" class="modal-title">Live-search status</h5>
                <button
                    type="button"
                    class="btn-close"
                    aria-label="Close"
                    @click="$refs.details.close()"
                />
            </div>
            <div class="modal-body">
                <p class="mb-1" :class="`text-${summary.color}`">
                    <strong>{{ summary.label }}</strong>
                </p>
                <p>{{ summaryMessage }}</p>
                <ul v-if="vendors.length" class="mb-0">
                    <li v-for="vendor in vendors" :key="vendor.source" class="py-1">
                        {{ vendorName(vendor.source) }}:
                        <span :class="`text-${appearance(vendor.status).color}`">{{
                            appearance(vendor.status).label
                        }}</span>
                    </li>
                </ul>
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="$refs.details.close()">
                    Close
                </button>
            </div>
        </dialog>
    </div>
</template>

<script>
    const appearances = {
        loading: { label: 'Loading saved search', color: 'primary' },
        checking: { label: 'Checking live-search status', color: 'primary' },
        running: { label: 'Searching', color: 'primary' },
        recovering: { label: 'Recovering results', color: 'primary' },
        complete: { label: 'Complete', color: 'success' },
        warning: { label: 'Needs attention', color: 'warning' },
        unknown: { label: 'Unconfirmed', color: 'warning' },
        error: { label: 'Failed', color: 'danger' },
    }

    export default {
        name: 'LiveSearchStatus',
        props: {
            loading: { type: Boolean, default: false },
            resumeFailed: { type: Boolean, default: false },
            runs: { type: Array, default: () => [] },
        },
        computed: {
            entries() {
                const entries = []
                if (this.loading || this.resumeFailed) {
                    entries.push({ status: this.loading ? 'loading' : 'unknown' })
                }
                this.runs.forEach((run) => {
                    const vendors = run.vendors || []
                    const activeStatus = ['checking', 'loading', 'recovering'].includes(
                        run.delivery
                    )
                        ? run.delivery
                        : 'running'
                    const healthy =
                        vendors.length && vendors.every((v) => v.status === 'complete' && !v.failed)
                    entries.push({
                        status:
                            !run.complete && !run.stopped
                                ? activeStatus
                                : run.complete && healthy
                                  ? 'complete'
                                  : 'warning',
                    })
                    vendors.forEach((vendor) =>
                        entries.push({
                            source: vendor.source,
                            status:
                                run.stopped && vendor.status === 'running'
                                    ? 'unknown'
                                    : vendor.status,
                        })
                    )
                })
                return entries
            },
            vendors() {
                // A vendor may appear in several searches; don't hide unfinished or failed work.
                const priority = ['running', 'error', 'unknown', 'warning', 'complete']
                const vendors = new Map()
                this.entries
                    .filter((entry) => entry.source)
                    .forEach((entry) => {
                        const previous = vendors.get(entry.source)
                        if (
                            !previous ||
                            priority.indexOf(entry.status) < priority.indexOf(previous.status)
                        ) {
                            vendors.set(entry.source, entry)
                        }
                    })
                return [...vendors.values()]
            },
            summaryStatus() {
                const statuses = this.entries.map((entry) => entry.status)
                return (
                    [
                        'loading',
                        'recovering',
                        'checking',
                        'running',
                        'error',
                        'warning',
                        'unknown',
                    ].find((status) => statuses.includes(status)) || 'complete'
                )
            },
            summaryMessage() {
                return {
                    loading: "We're loading your saved results.",
                    recovering: "We're checking for results that haven't appeared yet.",
                    checking: "We're checking whether your search has finished.",
                    running: "We're still waiting for some vendors to finish.",
                    error: "Some vendors couldn't complete the search. You can still use the results we found.",
                    warning:
                        "We couldn't confirm whether your search finished. Some results may be missing.",
                    unknown:
                        "We couldn't confirm whether your search finished. Some results may be missing.",
                    complete: 'All vendors have finished. Your results are ready.',
                }[this.summaryStatus]
            },
            summary() {
                const statuses = this.entries.map((entry) => entry.status)
                if (this.summaryStatus === 'running') {
                    const issues =
                        statuses.some((status) =>
                            ['error', 'warning', 'unknown'].includes(status)
                        ) ||
                        this.runs.some((run) => (run.vendors || []).some((vendor) => vendor.failed))
                    const label = this.runs.some(
                        (run) =>
                            run.resumed &&
                            !run.complete &&
                            !run.stopped &&
                            run.delivery === 'polling'
                    )
                        ? 'Live search still running'
                        : 'Searching'
                    return {
                        ...this.appearance('running'),
                        label: issues ? `${label} · needs attention` : label,
                    }
                }
                if (this.summaryStatus === 'error')
                    return { ...this.appearance('error'), label: 'Finished with errors' }
                if (['warning', 'unknown'].includes(this.summaryStatus))
                    return this.appearance('warning')
                return this.appearance(this.summaryStatus)
            },
        },
        methods: {
            vendorName(source) {
                const names = {
                    ilsmart: 'ILSmart',
                    boeing_api: 'Boeing',
                    parts_base: 'PartsBase',
                    textron: 'Textron',
                }
                return names[source] || source.replace(/_/g, ' ')
            },
            appearance(status) {
                return appearances[status] || appearances.unknown
            },
            closeOnBackdrop(event) {
                if (event.target === this.$refs.details) this.$refs.details.close()
            },
        },
    }
</script>

<style scoped>
    .search-status {
        margin-left: 16px;
        align-self: flex-start;
    }
    .live-status-label {
        font-weight: 600;
        color: var(--bs-body-color, #212529);
    }
    button.search-status-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 2px;
        border-radius: 6px;
        font-size: 0.875rem;
        color: inherit;
        background: transparent !important;
        border: 0 !important;
        box-shadow: none !important;
    }
    .search-status-button svg {
        flex-shrink: 0;
    }
    .search-status-button:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 2px;
    }
    .search-status-dialog {
        position: fixed;
        inset: 0;
        margin: auto;
        overflow: auto;
        width: min(480px, calc(100vw - 32px));
        max-height: 85vh;
        padding: 0;
        border: 1px solid var(--bs-border-color, #dee2e6);
        border-radius: 12px;
        color: var(--bs-body-color, #212529);
        background: var(--bs-body-bg, #fff);
    }
    .search-status-dialog::backdrop {
        background: rgb(0 0 0 / 45%);
    }
    .modal-header,
    .modal-body,
    .modal-footer {
        padding: 16px 24px;
    }
    .modal-header .btn-close {
        margin-left: auto;
    }
    .text-warning {
        color: var(--bs-warning-text-emphasis, #664d03) !important;
    }
</style>
